import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'

import { InfoIcon } from 'lucide-react'
import { inject, observer } from 'mobx-react'

import { IUploadLog, SourceEntityType } from '~/client/graph'
import ListTagCell, {
  TagCellItemKeyEnum,
} from '~/client/src/desktop/components/ListTagCell'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import CompanyProfilePreview from '~/client/src/shared/components/CompanyProfilePreview/CompanyProfilePreview'
import * as Icons from '~/client/src/shared/components/Icons'
import { LWFCRowData } from '~/client/src/shared/components/ListWithFixedColumns/VirtualListHelpers'
import VirtualMultiGrid from '~/client/src/shared/components/ListWithFixedColumns/VirtualMultiGrid'
import SitemapAttributeTag from '~/client/src/shared/components/SitemapAttributeTag/SitemapAttributeTag'
import StatusBadge from '~/client/src/shared/components/StatusBadge/StatusBadge'
import TooltipWrapper from '~/client/src/shared/components/TooltipWrapper/TooltipWrapper'
import UserProfilePreview from '~/client/src/shared/components/UserProfilePreview/UserProfilePreview'
import WorkflowTypeIcon from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowTypeIcon'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Workflow from '~/client/src/shared/models/Workflow'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { DASH } from '~/client/src/shared/utils/usefulStrings'

import AttachmentsStore, { AttachmentColumns } from '../../Attachments.store'
import AttachmentHoverPreview from '../AttachmentPreview/AttachmentHoverPreview'
import AttachmentIconWithPreview from '../AttachmentPreview/AttachmentIconWithPreview'
import FileCategoryIcon from './FileCategoryIcon'
import FileTypeIcon from './FileTypeIcon'

interface IProps {
  store: AttachmentsStore
  projectDateStore?: ProjectDateStore
  projectMembersStore?: ProjectMembersStore
  companiesStore?: CompaniesStore
  locationAttributesStore?: LocationAttributesStore
  state?: DesktopInitialState
  workflowTypesStore?: WorkflowTypesStore
  workflowCategoriesStore?: WorkflowCategoriesStore
  userProjectsStore?: UserProjectsStore
  eventsStore?: DesktopEventStore
  workflows?: Map<string, Workflow>
}

const DEFAULT_COLUMN_ELLIPSIS_OFFSET = 24
const NAME_COLUMN_ELLIPSIS_OFFSET = 80
const COLUMN_WITH_ICON_OFFSET = 44

const AttachmentsListComponent: React.FC<IProps> = ({
  store,
  projectDateStore,
  projectMembersStore,
  companiesStore,
  locationAttributesStore,
  state,
  workflowTypesStore,
  workflowCategoriesStore,
  userProjectsStore,
  eventsStore,
}) => {
  const [hoveredAttachment, setHoveredAttachment] = useState<IUploadLog | null>(
    null,
  )
  const [hoverPosition, setHoverPosition] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  })

  const onCellClick = useCallback(
    (rowData: LWFCRowData, columnKey: string): void => {
      if (columnKey === AttachmentColumns.SOURCE) {
        const attachmentData = rowData[AttachmentColumns.NAME]
        if (attachmentData?.uploadLog) {
          const { sourceEntityType, sourceEntityId } = attachmentData.uploadLog

          if (sourceEntityType === SourceEntityType.Form) {
            store.openFormDetailsPanel(store.workflows.get(sourceEntityId))
          } else if (sourceEntityType === SourceEntityType.Delivery) {
            store.openDeliveryDetailsPanel(sourceEntityId)
          }
        }
        return
      }

      const attachmentData = rowData[AttachmentColumns.NAME]
      if (attachmentData?.uploadLog) {
        store.closeFormDetailsPanel()
        store.closeDeliveryDetailsPanel()
        store.openAttachmentModal(attachmentData.uploadLog)
      }
    },
    [store, state, eventsStore],
  )

  const handleIconMouseEnter = useCallback(
    (uploadLog: IUploadLog, event: React.MouseEvent): void => {
      setHoveredAttachment(uploadLog)
      const rect = event.currentTarget.getBoundingClientRect()
      setHoverPosition({ x: rect.right, y: rect.bottom })
    },
    [],
  )

  const handleIconMouseLeave = useCallback((): void => {
    setHoveredAttachment(null)
    setHoverPosition({ x: 0, y: 0 })
  }, [])

  useEffect(() => {
    const updateAttachmentModalRowClass = () => {
      const allCells = document.querySelectorAll('.cell.attachment-modal-open')
      allCells.forEach(cell => {
        cell.classList.remove('attachment-modal-open')
      })

      if (store.isAttachmentModalOpen && store.selectedAttachment) {
        const rowIndex = store.rows.findIndex(row => {
          if (!row.data) return false
          const nameData = row.data[AttachmentColumns.NAME]
          return nameData?.uploadLog?.id === store.selectedAttachment?.id
        })

        if (rowIndex !== -1) {
          const actualRowIndex = rowIndex + 1
          const cells = document.querySelectorAll(
            `[data-row-index="${actualRowIndex}"]`,
          )
          cells.forEach(cell => {
            if (
              cell.classList.contains('cell') &&
              !cell.classList.contains('category-cell') &&
              !cell.classList.contains('cell-header')
            ) {
              cell.classList.add('attachment-modal-open')
            }
          })
        }
      }
    }

    updateAttachmentModalRowClass()
  }, [store.isAttachmentModalOpen, store.selectedAttachment, store.rows])

  const renderCheckbox = useCallback((): JSX.Element => {
    return (
      <div className="row y-center x-center full-height full-width">
        <input
          type="checkbox"
          disabled
          style={{ width: '16px', height: '16px' }}
        />
      </div>
    )
  }, [])

  const renderName = useCallback(
    (data: any): JSX.Element => {
      const { fileType, fileName, uploadLog } = data

      return (
        <div
          className="row y-center full-height"
          style={{
            maxWidth:
              store.getColumnWidth(AttachmentColumns.NAME) -
              DEFAULT_COLUMN_ELLIPSIS_OFFSET,
            minWidth:
              store.getColumnWidth(AttachmentColumns.NAME) -
              DEFAULT_COLUMN_ELLIPSIS_OFFSET,
          }}
        >
          <Icons.UnfilledStarInactive className="mr8 no-grow" />
          <div
            onMouseEnter={event => handleIconMouseEnter(uploadLog, event)}
            onMouseLeave={handleIconMouseLeave}
            className="no-grow pointer"
          >
            <AttachmentIconWithPreview
              uploadLog={uploadLog}
              fileName={fileName}
              fileType={fileType}
              className="mr8 no-grow"
            />
          </div>
          <TooltipWrapper className="wrapped-popover" content={fileName}>
            <div
              className="text large truncate underline pointer"
              style={{
                maxWidth:
                  store.getColumnWidth(AttachmentColumns.NAME) -
                  NAME_COLUMN_ELLIPSIS_OFFSET,
                minWidth:
                  store.getColumnWidth(AttachmentColumns.NAME) -
                  NAME_COLUMN_ELLIPSIS_OFFSET,
              }}
            >
              <div className="text large ellipsis">{fileName}</div>
            </div>
          </TooltipWrapper>
        </div>
      )
    },
    [handleIconMouseEnter, handleIconMouseLeave],
  )

  const renderFileType = useCallback((data: string): JSX.Element => {
    return (
      <div
        className="text large row y-center full-height full-width"
        style={{
          maxWidth:
            store.getColumnWidth(AttachmentColumns.FILE_TYPE) -
            DEFAULT_COLUMN_ELLIPSIS_OFFSET,
          minWidth:
            store.getColumnWidth(AttachmentColumns.FILE_TYPE) -
            DEFAULT_COLUMN_ELLIPSIS_OFFSET,
        }}
      >
        <FileTypeIcon fileType={data} className="mr4 form-type-icon-size" />
        <div
          style={{
            maxWidth:
              store.getColumnWidth(AttachmentColumns.FILE_TYPE) -
              COLUMN_WITH_ICON_OFFSET,
            minWidth:
              store.getColumnWidth(AttachmentColumns.FILE_TYPE) -
              COLUMN_WITH_ICON_OFFSET,
          }}
        >
          <div className="text large ellipsis">{data.toUpperCase()}</div>
        </div>
      </div>
    )
  }, [])

  const renderFileCategory = useCallback((data: string): JSX.Element => {
    return (
      <div
        className="text large row y-center full-height"
        style={{
          maxWidth:
            store.getColumnWidth(AttachmentColumns.FILE_CATEGORY) -
            DEFAULT_COLUMN_ELLIPSIS_OFFSET,
          minWidth:
            store.getColumnWidth(AttachmentColumns.FILE_CATEGORY) -
            DEFAULT_COLUMN_ELLIPSIS_OFFSET,
        }}
      >
        <FileCategoryIcon
          fileCategory={data}
          className="mr4 form-type-icon-size"
        />
        <div
          style={{
            maxWidth:
              store.getColumnWidth(AttachmentColumns.FILE_CATEGORY) -
              DEFAULT_COLUMN_ELLIPSIS_OFFSET,
            minWidth:
              store.getColumnWidth(AttachmentColumns.FILE_CATEGORY) -
              DEFAULT_COLUMN_ELLIPSIS_OFFSET,
          }}
        >
          <div className="text large ellipsis">{data}</div>
        </div>
      </div>
    )
  }, [])

  const renderWorkflowId = useCallback((data: string): JSX.Element => {
    return (
      <div
        className="text large underline pointer full-height row y-center"
        style={{
          maxWidth:
            store.getColumnWidth(AttachmentColumns.SOURCE) -
            DEFAULT_COLUMN_ELLIPSIS_OFFSET,
          minWidth:
            store.getColumnWidth(AttachmentColumns.SOURCE) -
            DEFAULT_COLUMN_ELLIPSIS_OFFSET,
        }}
      >
        <div className="text large ellipsis">{data}</div>
      </div>
    )
  }, [])

  const renderWorkflowType = useCallback(
    (data: IUploadLog): JSX.Element => {
      const { sourceEntityType, sourceEntityTypeName, sourceEntityId } = data
      const workflow = store.workflows.get(sourceEntityId)

      if (sourceEntityType === SourceEntityType.Form) {
        return (
          <div className="row y-center full-height">
            <WorkflowTypeIcon
              iconName={workflow?.getIconNameOfPermitType(workflowTypesStore)}
              className="row no-grow form-type-icon-size mr4"
            />
            <div
              style={{
                maxWidth:
                  store.getColumnWidth(AttachmentColumns.FORM_TYPE) -
                  COLUMN_WITH_ICON_OFFSET,
                minWidth:
                  store.getColumnWidth(AttachmentColumns.FORM_TYPE) -
                  COLUMN_WITH_ICON_OFFSET,
              }}
            >
              <div className="text large ellipsis">
                {workflow?.getNameOfPermitType(workflowTypesStore) ||
                  sourceEntityTypeName ||
                  sourceEntityType}
              </div>
            </div>
          </div>
        )
      }

      return (
        <div className="row y-center full-height full-width">
          <Icons.DeliveryFilled className="mr4 text-grey-light no-grow form-type-icon-size" />
          <div className="text large">{sourceEntityType}</div>
        </div>
      )
    },
    [workflowTypesStore],
  )

  const renderWorkflowTitle = useCallback((data: IUploadLog): JSX.Element => {
    const { sourceEntityType, sourceEntityId } = data
    const workflow = store.workflows.get(sourceEntityId)

    if (sourceEntityType === SourceEntityType.Form && workflow?.title) {
      return (
        <div
          className="full-height row y-center"
          style={{
            maxWidth:
              store.getColumnWidth(AttachmentColumns.WORKFLOW_TITLE) -
              DEFAULT_COLUMN_ELLIPSIS_OFFSET,
            minWidth:
              store.getColumnWidth(AttachmentColumns.WORKFLOW_TITLE) -
              DEFAULT_COLUMN_ELLIPSIS_OFFSET,
          }}
        >
          <span className="text large ellipsis">{workflow.title}</span>
        </div>
      )
    }

    return (
      <div className="text large full-height full-width row y-center">-</div>
    )
  }, [])

  const renderWorkflowStatus = useCallback((data: IUploadLog): JSX.Element => {
    const { sourceEntityId } = data
    const workflow = store.workflows.get(sourceEntityId)
    if (!workflow) {
      return (
        <div className="text large full-height full-width row y-center">-</div>
      )
    }

    const { status, workflowStepLevel, isLate } = workflow
    return (
      <div className="mx12 full-height full-width row y-center no-flex-children">
        <StatusBadge
          status={status}
          workflowStepLevel={workflowStepLevel}
          isLate={isLate}
        />
      </div>
    )
  }, [])

  const renderLocation = useCallback(
    (data: IUploadLog): JSX.Element => {
      const { locationIds, sourceEntityId } = data
      const workflow = store.workflows.get(sourceEntityId)
      const workflowLocations = workflow?.locations || []

      const hasWorkflowLocations = workflowLocations.length > 0
      // By id: the kind stored beside it on the workflow value or the upload is a
      // snapshot that goes stale on a re-kind (STX1-597)
      const areAllWorkflowLocationsUnassigned = workflowLocations.every(
        location => !locationAttributesStore.getById(location.id),
      )
      const hasLocationIds = locationIds && locationIds.length > 0

      if (
        (!hasWorkflowLocations && !hasLocationIds) ||
        areAllWorkflowLocationsUnassigned
      ) {
        const { projectLocation } = locationAttributesStore
        return (
          <SitemapAttributeTag
            shouldShowAsTag={false}
            contentContainerClassName="text-ellipsis py2 row name-cell"
            className="full-height full-width"
            dataObject={projectLocation}
            shouldUseAlternativeIcon
          >
            {Localization.translator.project}
          </SitemapAttributeTag>
        )
      }

      const locations = hasWorkflowLocations
        ? workflowLocations
            .map(locationObj => locationAttributesStore.getById(locationObj.id))
            .filter(Boolean)
        : locationIds
            ?.map(id => locationAttributesStore.getById(id))
            .filter(Boolean) || []

      return (
        <ListTagCell
          items={locations}
          renderItemKey={TagCellItemKeyEnum.LOCATION}
          containerStyle={{
            maxWidth:
              store.getColumnWidth(AttachmentColumns.LOCATION) -
              DEFAULT_COLUMN_ELLIPSIS_OFFSET,
            minWidth:
              store.getColumnWidth(AttachmentColumns.LOCATION) -
              DEFAULT_COLUMN_ELLIPSIS_OFFSET,
          }}
        />
      )
    },
    [locationAttributesStore, projectDateStore],
  )

  const renderDateUploaded = useCallback(
    (data: number): JSX.Element => {
      const { getMonthDayYearAndTimeToDisplay } = projectDateStore

      const date = getMonthDayYearAndTimeToDisplay(data)

      return (
        <div
          className="text large"
          style={{
            maxWidth:
              store.getColumnWidth(AttachmentColumns.DATE_UPLOADED) -
              DEFAULT_COLUMN_ELLIPSIS_OFFSET,
            minWidth:
              store.getColumnWidth(AttachmentColumns.DATE_UPLOADED) -
              DEFAULT_COLUMN_ELLIPSIS_OFFSET,
          }}
        >
          <div className="text large ellipsis">{date}</div>
        </div>
      )
    },
    [projectDateStore],
  )

  const renderUploadedBy = useCallback(
    (data: IUploadLog): JSX.Element => {
      const { getById } = projectMembersStore
      const { userId, userName } = data

      const user = getById(userId)

      if (!user) {
        return <div className="text large">{userName || DASH}</div>
      }

      return (
        <UserProfilePreview
          user={user}
          showBoldName={false}
          isUserNameClickable
          isAlwaysUnderlined
          isBriefInfoHidden
        />
      )
    },
    [projectMembersStore],
  )

  const renderCompany = useCallback(
    (data: IUploadLog): JSX.Element => {
      const { getCompanyById } = companiesStore
      const { userCompanyId, userCompanyName, userId } = data

      const companyFromDate = getCompanyById(userCompanyId)
      const member = projectMembersStore.getById(userId)
      const userProject = member && userProjectsStore.getByUser(member)
      const companyFromMember =
        userProject && getCompanyById(userProject?.companyId)

      if (!companyFromDate && !companyFromMember) {
        return <div className="text large">{userCompanyName || DASH}</div>
      }

      return (
        <CompanyProfilePreview
          company={companyFromMember || companyFromDate}
          shouldShowCompanyType={false}
          isMenuHidden
          isPhoneHidden
          contentContainerClassName="underline"
        />
      )
    },
    [companiesStore],
  )

  const renderWorkflowCategory = useCallback(
    (data: IUploadLog): JSX.Element => {
      const { sourceEntityTypeName, sourceEntityType, sourceEntityId } = data
      const workflow = store.workflows.get(sourceEntityId)
      const category = workflow?.getCategoryCaption(workflowCategoriesStore)
      const isDefaultCategory = workflowCategoriesStore.getById(
        workflow?.categoryId,
      )?.isDefaultCategory

      if (sourceEntityType === SourceEntityType.Form) {
        return (
          <div className="row text large">
            {isDefaultCategory && (
              <Icons.LogoDark className="row no-grow mr5 default-category-icon" />
            )}
            <div
              className="row"
              style={{
                maxWidth:
                  store.getColumnWidth(AttachmentColumns.CATEGORY) -
                  (isDefaultCategory
                    ? COLUMN_WITH_ICON_OFFSET
                    : DEFAULT_COLUMN_ELLIPSIS_OFFSET),
                minWidth:
                  store.getColumnWidth(AttachmentColumns.CATEGORY) -
                  (isDefaultCategory
                    ? COLUMN_WITH_ICON_OFFSET
                    : DEFAULT_COLUMN_ELLIPSIS_OFFSET),
              }}
            >
              <div className="text large ellipsis row">
                {category || sourceEntityTypeName}
                {isDefaultCategory && (
                  <TooltipWrapper
                    content={Localization.translator.categoryCreatedByStruxHub}
                    className="row no-grow ml4"
                  >
                    <InfoIcon className="tw:size-3 tw:shrink-0 tw:text-muted-foreground" />
                  </TooltipWrapper>
                )}
              </div>
            </div>
          </div>
        )
      }
      return <div className="text large row y-center ">-</div>
    },
    [workflowCategoriesStore],
  )

  const getCellRenderers = useCallback(() => {
    return {
      [AttachmentColumns.CHECKBOX]: () => renderCheckbox(),
      [AttachmentColumns.NAME]: (data: any) => renderName(data),
      [AttachmentColumns.FILE_TYPE]: (data: string) => renderFileType(data),
      [AttachmentColumns.FILE_CATEGORY]: (data: string) =>
        renderFileCategory(data),
      [AttachmentColumns.SOURCE]: (data: string) => renderWorkflowId(data),
      [AttachmentColumns.FORM_TYPE]: (data: IUploadLog) =>
        renderWorkflowType(data),
      [AttachmentColumns.WORKFLOW_TITLE]: (data: IUploadLog) =>
        renderWorkflowTitle(data),
      [AttachmentColumns.WORKFLOW_STATUS]: (data: IUploadLog) =>
        renderWorkflowStatus(data),
      [AttachmentColumns.LOCATION]: (data: IUploadLog) => renderLocation(data),
      [AttachmentColumns.DATE_UPLOADED]: (data: number) =>
        renderDateUploaded(data),
      [AttachmentColumns.UPLOADED_BY]: (data: IUploadLog) =>
        renderUploadedBy(data),
      [AttachmentColumns.COMPANY]: (data: IUploadLog) => renderCompany(data),
      [AttachmentColumns.CATEGORY]: (data: IUploadLog) =>
        renderWorkflowCategory(data),
    }
  }, [
    renderCheckbox,
    renderName,
    renderFileType,
    renderFileCategory,
    renderWorkflowId,
    renderWorkflowType,
    renderWorkflowTitle,
    renderWorkflowStatus,
    renderLocation,
    renderDateUploaded,
    renderUploadedBy,
    renderCompany,
    renderWorkflowCategory,
  ])

  const cellRenderer = useCallback(
    (columnKey: string, value: any) => {
      const renderers = getCellRenderers()
      if (renderers[columnKey]) {
        return renderers[columnKey](value)
      }
      return String(value || DASH)
    },
    [getCellRenderers],
  )

  return (
    <div className="attachments-list full-height">
      <VirtualMultiGrid
        rows={store.rows}
        columns={store.columns}
        fixedColumnsCount={store.fixedColumnsCount}
        cellRenderer={cellRenderer}
        onCellClick={onCellClick}
        collapsedCategories={store.collapsedCategories}
        onCategoryCollapsingToggle={store.toggleCategoryCollapsing}
        columnsWidthState={store.columnsWidthState}
        selectedItemId={store.selectedAttachment?.id}
        highlightOpenItemRow
      />

      <AttachmentHoverPreview
        uploadLog={hoveredAttachment}
        isVisible={!!hoveredAttachment}
        position={hoverPosition}
      />
    </div>
  )
}

const AttachmentsList = inject(
  'projectDateStore',
  'projectMembersStore',
  'companiesStore',
  'locationAttributesStore',
  'workflowTypesStore',
  'workflowCategoriesStore',
  'userProjectsStore',
  'state',
  'eventsStore',
)(observer(AttachmentsListComponent))

export default AttachmentsList
